<template>
  <div class="vp-root">
    <div v-if="regression.status === 'loading'" class="vp-loading">
      <b-loading :active="true" :is-full-page="false"></b-loading>
    </div>

    <div v-else-if="regression.status === 'error'" class="vp-error">
      {{ regression.error || "El análisis no pudo cargarse." }}
    </div>

    <div v-else-if="regression.status === 'success' && regression.data" class="vp-content">
      <div v-if="viewMode === 'red'" class="vp-tabs-row">
        <effect-card
          v-for="e in effectKeys"
          :key="e.apiKey"
          :effect-key="e.apiKey"
          :label="e.label"
          :analysis="regression.data.results[e.apiKey]"
          :selected="selectedEffect === e.apiKey"
          v-on:select="onSelectEffect"
        />
      </div>

      <div class="vp-panel" :class="{ 'vp-panel--no-tabs': viewMode !== 'red' }">
        <div class="vp-panel-header">
          <div class="vp-view-switch">
            <span
              class="vp-view-switch__opt"
              :class="{ 'vp-view-switch__opt--active': viewMode === 'red' }"
              v-on:click="viewMode = 'red'"
              >Red</span
            >
            <span
              class="vp-view-switch__opt"
              :class="{ 'vp-view-switch__opt--active': viewMode === 'sankey' }"
              v-on:click="viewMode = 'sankey'"
              >Sankey</span
            >
          </div>
          <div class="vp-provenance">{{ provenanceText }}</div>
        </div>

        <relations-network
          v-if="viewMode === 'red' && selectedEffect"
          :key="selectedEffect"
          :effect="selectedEffect"
          :results="regression.data.results"
        />
        <p v-else-if="viewMode === 'red'" class="vp-empty">
          Selecciona una tarjeta con modelo disponible para ver su Red de relaciones.
        </p>
        <sankey-diagram v-else-if="viewMode === 'sankey'" :results="regression.data.results" />
      </div>
    </div>
  </div>
</template>

<script>
import { EFFECT_KEYS } from "./regression-helpers";
import EffectCard from "./EffectCard.vue";
import RelationsNetwork from "./RelationsNetwork.vue";
import SankeyDiagram from "./SankeyDiagram.vue";

export default {
  name: "VariablesPrioritarias",
  components: {
    EffectCard,
    RelationsNetwork,
    SankeyDiagram,
  },
  props: {
    frsm: {
      type: Object,
      default: null,
    },
    year: {
      type: [String, Number],
      default: "",
    },
    regression: {
      type: Object,
      required: true,
    },
    rangoFp: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      effectKeys: EFFECT_KEYS,
      selectedEffect: null,
      viewMode: "red",
    };
  },
  computed: {
    provenanceText() {
      if (!this.regression.data) return "";
      const engine = this.regression.data.engine_version || "—";
      const main =
        this.selectedEffect && this.regression.data.results[this.selectedEffect];
      const n = main && main.n != null ? main.n : null;
      const yearText = this.year ? this.year : "más reciente disponible";
      let text = "Cálculo " + engine;
      if (n != null) text += " · " + n + " respondientes";
      text += " · año " + yearText;
      return text;
    },
  },
  watch: {
    "regression.status": function(status) {
      if (status === "success") {
        this.pickDefaultEffect();
      }
    },
  },
  created() {
    if (this.regression.status === "idle") {
      this.$emit("fetch");
    }
    if (this.regression.status === "success") {
      this.pickDefaultEffect();
    }
  },
  methods: {
    pickDefaultEffect() {
      if (this.selectedEffect || !this.regression.data) return;
      const results = this.regression.data.results;
      const firstEnabled = this.effectKeys.filter(function(e) {
        return results[e.apiKey] && results[e.apiKey].no_model === false;
      })[0];
      if (firstEnabled) this.selectedEffect = firstEnabled.apiKey;
    },
    onSelectEffect(effectKey) {
      this.selectedEffect = effectKey;
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap");

.vp-root {
  --line: #ddd;
  --surface-card: #ffffff;
  --no-model-ink: #a12a2a;
  --brand: #510087;
  --accent: #9246df;

  font-family: "Inter", sans-serif;
}

.vp-loading {
  position: relative;
  min-height: 220px;
}

.vp-error {
  padding: 24px;
  color: #a12a2a;
  text-align: center;
}

.vp-empty {
  padding: 24px;
  color: #7a7a7a;
  text-align: center;
}

.vp-tabs-row {
  display: flex;
  align-items: flex-end;
  gap: 26px;
}

.vp-panel {
  border: 1.5px solid var(--line);
  border-top-left-radius: 0;
  border-top-right-radius: 10px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background: var(--surface-card);
  padding: 20px;
}

// Sin tabs (vista Sankey) no hay tab activa "mordiendo" la esquina superior
// izquierda del panel — vuelve a su radio normal, mismo criterio que el
// selector `.eff-tabstrip + .card` del Artifact (solo aplica cuando el
// tabstrip existe justo antes del panel).
.vp-panel--no-tabs {
  border-top-left-radius: 10px;
}

.vp-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.vp-view-switch {
  display: inline-flex;
  align-items: center;
  background: #f3f3f3;
  border-radius: 999px;
  padding: 2px;
  gap: 1px;
  font-size: 0.68rem;
}

.vp-view-switch__opt {
  padding: 5px 13px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #9a94a3;
}

.vp-view-switch__opt--active {
  background: var(--surface-card);
  color: var(--brand);
  box-shadow: 0 1px 2px rgba(20, 10, 30, 0.1);
}

.vp-provenance {
  font-size: 0.72rem;
  color: #9aa0a6;
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/variables-prioritarias/VariablesPrioritarias.vue